import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../api/adminApi';
import { getToolLabel } from '../../shared/toolLabels';

/**
 * JSDoc Header: UserEditModal Component
 * 
 * Renders a modal window to create or edit a user.
 * Handles user metadata (name, username, password, role), activation/deactivation,
 * password resetting, user deletion, tool assignment, and granular tool permissions.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Object} props.user - The user being edited, or null for creating a new user
 * @param {number|string} props.currentUserId - The ID of the currently logged-in admin user
 * @param {Array<Object>} props.companyTools - List of available tools for the company
 * @param {Array<Object>} props.users - List of all users in the system (for delegation settings)
 * @param {Object} props.stats - Usage statistics for limits checks
 * @param {Function} props.onSaveSuccess - Callback called on successful save or deletion to refresh user data
 */
export default function UserEditModal({
  isOpen,
  onClose,
  user,
  currentUserId,
  companyTools = [],
  users = [],
  stats = {},
  onSaveSuccess
}) {
  const modalMode = user ? 'edit' : 'create';

  // Modal Sub-Views State
  const [editingTool, setEditingTool] = useState(null); // The tool_key currently being configured
  const [showToolList, setShowToolList] = useState(false); // Sub-view for tool toggle list
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamSearchTerm, setTeamSearchTerm] = useState('');

  // Loaded User Data (Edit Mode)
  const [userTools, setUserTools] = useState([]); // Array of tool_keys
  const [userToolPermissions, setUserToolPermissions] = useState({}); // Map: tool_key -> permissionObject

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    role: 'employee',
    is_active: true
  });

  const [pendingToolChanges, setPendingToolChanges] = useState({});
  const [pendingPermissionChanges, setPendingPermissionChanges] = useState({});

  // Reset form and sub-views on open/change
  useEffect(() => {
    if (isOpen) {
      setEditingTool(null);
      setShowToolList(false);
      setShowTeamModal(false);
      setTeamSearchTerm('');
      setPendingToolChanges({});
      setPendingPermissionChanges({});

      if (user) {
        setFormData({
          name: user.name || '',
          username: user.username || '',
          role: user.role || 'employee',
          password: '',
          is_active: user.is_active !== false
        });
        fetchUserTools(user.id);
      } else {
        setFormData({
          name: '',
          username: '',
          role: 'employee',
          password: '',
          is_active: true
        });
        setUserTools([]);
        setUserToolPermissions({});
      }
    }
  }, [isOpen, user]);

  // Helper: Tools für einen User laden
  const fetchUserTools = async (userId) => {
    try {
      const data = await adminApi.getUserTools(userId);
      setUserTools(data.grantedTools || []);
      setUserToolPermissions(data.toolPermissions || {});
    } catch (err) {
      console.error(err);
      alert('Fehler beim Laden der User-Tools');
    }
  };

  if (!isOpen) return null;

  // 4. Speichern (Create & Update)
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'create') {
        // 1. HARTE GRENZE: Gesamt-Accounts (Speicherplatz)
        if (stats.max_total_users !== -1 && stats.current_total_users >= stats.max_total_users) {
          alert(`⛔ Account-Limit erreicht! Dein Paket erlaubt maximal ${stats.max_total_users} gespeicherte Nutzer (aktiv + inaktiv).\nBitte lösche alte Nutzer endgültig, um Platz zu schaffen, oder buche ein größeres Paket.`);
          return;
        }

        // 2. WARNUNG: Lizenz-Limit (Aktive Nutzer)
        if (stats.max_users !== -1 && stats.current_users >= stats.max_users) {
          const proceed = window.confirm(`⚠️ Lizenz-Limit erreicht (${stats.max_users} aktive Nutzer).\nDu kannst nur einen INAKTIVEN Nutzer anlegen.\nFortfahren?`);
          if (!proceed) return;
        }

        await adminApi.createUser(formData);
        alert('Nutzer erstellt!');
      } else {
        await adminApi.updateUser(user.id, formData);

        // Tools speichern (Enable/Disable)
        for (const [toolKey, shouldGrant] of Object.entries(pendingToolChanges)) {
          if (shouldGrant) {
            await adminApi.assignUserTool(user.id, toolKey);
          } else {
            await adminApi.removeUserTool(user.id, toolKey);
          }
        }

        // Granulare Berechtigungen speichern
        for (const [toolKey, newPerms] of Object.entries(pendingPermissionChanges)) {
          await adminApi.updateUserToolPermissions(user.id, toolKey, { permissions: newPerms });
        }

        alert('✅ Änderungen gespeichert!');

        // REFRESH DATA to keep UI in sync if we stay open
        await fetchUserTools(user.id);
      }

      // Refresh list in parent component
      onSaveSuccess();

      // Navigation Logic:
      if (editingTool) {
        setEditingTool(null); // Back to Tool List
      } else if (showToolList) {
        // Stay in Tool List
      } else {
        onClose(); // Close modal
      }

      setPendingToolChanges({});
      setPendingPermissionChanges({});
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  // Tools Toggle (Lokal im Modal)
  const handleToggleTool = (toolKey, isCurrentlyGranted) => {
    const isActive = pendingToolChanges[toolKey] !== undefined ? pendingToolChanges[toolKey] : isCurrentlyGranted;
    const actionText = isActive ? 'entziehen' : 'freischalten';

    if (!window.confirm(`Möchtest du diesem Nutzer den Zugriff auf dieses Tool wirklich ${actionText}?`)) {
      return;
    }

    const newPending = { ...pendingToolChanges };
    newPending[toolKey] = !isActive;
    setPendingToolChanges(newPending);
  };

  // User Status Toggle (Aktivieren/Deaktivieren)
  const handleToggleUserStatus = async () => {
    if (!user) return;
    const isActive = user.is_active !== false; // Default true
    const actionText = isActive ? 'deaktivieren' : 'aktivieren';

    if (!window.confirm(`Möchtest du diesen Nutzer wirklich ${actionText}?`)) {
      return;
    }

    // Limit Check beim Aktivieren vorab Frontend
    if (!isActive && stats.max_users !== -1 && stats.current_users >= stats.max_users) {
      alert("Limit erreicht! Kann Nutzer nicht aktivieren.");
      return;
    }

    try {
      if (isActive) {
        const data = await adminApi.deactivateUser(user.id);
        alert(`Nutzer ${data?.name || ''} deaktiviert.`);
      } else {
        const data = await adminApi.activateUser(user.id);
        alert(`Nutzer ${data?.name || ''} aktiviert!`);
      }
      onSaveSuccess();
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Status-Toggle');
    }
  };

  // Löschen
  const handleDeleteFinal = async () => {
    if (!window.confirm('ACHTUNG: Nutzer permanent löschen? Daten gehen verloren!')) return;
    try {
      await adminApi.deleteUser(user.id);
      onSaveSuccess();
      onClose();
    } catch (err) {
      alert('Fehler beim Löschen');
    }
  };

  // PW Reset
  const handleChangePasswordForce = async () => {
    const newPw = prompt("Neues temporäres Passwort:");
    if (!newPw) return;
    try {
      await adminApi.changeUserPassword(user.id, newPw);
      alert("Passwort geändert.");
    } catch (err) {
      alert("Fehler beim Ändern");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '600px', width: '90%' }}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {(editingTool || showToolList) && (
              <button
                onClick={() => {
                  if (editingTool) setEditingTool(null);
                  else setShowToolList(false);
                }}
                className="back-btn"
                title="Zurück"
              >
                ← Zurück
              </button>
            )}
            <div>
              <h3 style={{ marginBottom: '2px' }}>
                {editingTool
                  ? (editingTool === 'journal' ? 'Einstellungen: Projektverwaltung' : `Einstellungen: ${getToolLabel(editingTool, companyTools.find(t => t.tool_key === editingTool)?.tool_name)}`)
                  : (showToolList ? 'Tool-Verwaltung' : (modalMode === 'create' ? 'Neuen Nutzer anlegen' : 'Nutzer bearbeiten'))}
              </h3>
              {modalMode === 'edit' && user && (
                <div style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: '500' }}>
                  {user.name}
                </div>
              )}
            </div>
          </div>
          <button className="close-modal" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {/* === VIEW 1: USER DETAILS (Main) === */}
          {!showToolList && !editingTool && (
            <>
              <form onSubmit={handleSave}>
                <div className="form-column">
                  <h4 className="section-title">Stammdaten</h4>
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      required
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Max Mustermann"
                    />
                  </div>

                  <div className="form-group">
                    <label>Benutzername</label>
                    <input
                      required
                      type="text"
                      value={formData.username}
                      onChange={e => setFormData({ ...formData, username: e.target.value })}
                      placeholder="Login-Name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Systemberechtigung</label>
                    <select
                      value={formData.role}
                      onChange={e => setFormData({ ...formData, role: e.target.value })}
                    >
                      <option value="employee">Mitarbeiter</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  {modalMode === 'create' && (
                    <div className="form-group">
                      <label>Initial-Passwort</label>
                      <input
                        required
                        type="password"
                        minLength="6"
                        value={formData.password}
                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                        placeholder="••••••"
                      />
                    </div>
                  )}

                  {/* Button to Open Tool Management (Only Edit Mode) */}
                  {modalMode === 'edit' && (
                    <div className="form-group" style={{ marginTop: '25px' }}>
                      <button
                        type="button"
                        className="btn-secondary-action"
                        onClick={() => setShowToolList(true)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: '#f3f4f6',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          color: '#374151'
                        }}
                      >
                        🛠️ Tool-Verwaltung öffnen
                      </button>
                    </div>
                  )}
                </div>

                <div className="modal-actions" style={{ marginTop: '30px' }}>
                  <button type="button" className="btn-cancel" onClick={onClose}>Abbrechen</button>
                  <button type="submit" className="btn-save">Speichern</button>
                </div>
              </form>

              {/* DANGER ZONE */}
              {modalMode === 'edit' && (
                <div className="danger-zone" style={{ marginTop: '30px' }}>
                  <h4>Verwaltung</h4>
                  {user.is_primary_admin ? (
                    <div style={{ padding: '10px', background: '#f3f4f6', borderRadius: '6px', color: '#666', fontSize: '0.9rem' }}>
                      🛡️ Haupt-Account (kann nicht gelöscht werden).
                      <div style={{ marginTop: '10px' }}>
                        <button className="btn-warning" onClick={handleChangePasswordForce} type="button" style={{ width: '100%' }}>PW Reset</button>
                      </div>
                    </div>
                  ) : (
                    <div className="danger-actions">
                      <button
                        className={user.is_active === false ? "btn-activate" : "btn-danger"}
                        onClick={handleToggleUserStatus}
                        type="button"
                      >
                        {user.is_active === false ? "Nutzer aktivieren" : "Deaktivieren"}
                      </button>
                      <button className="btn-warning" onClick={handleChangePasswordForce} type="button">PW Reset</button>
                      <button className="btn-critical" onClick={handleDeleteFinal} type="button">Löschen</button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* === VIEW 2: TOOL LIST === */}
          {showToolList && !editingTool && (
            <div className="tool-list-view">
              <h4 className="section-title">Tools & Zugriff</h4>
              <p style={{ marginBottom: '20px', color: '#666', fontSize: '0.9rem' }}>
                Aktiviere oder deaktiviere Tools für diesen Nutzer. Klicke auf "Einstellungen", um detaillierte Rechte zu vergeben.
              </p>
              <div className="tool-list">
                {companyTools.filter(tool => tool.enabled).map(tool => {
                  const isGranted = userTools.includes(tool.tool_key);
                  const finalState = pendingToolChanges[tool.tool_key] !== undefined ? pendingToolChanges[tool.tool_key] : isGranted;
                  return (
                    <div key={tool.tool_key} className={`tool-item ${finalState ? 'active' : ''}`}>
                      <div style={{ flex: 1 }}>
                        <strong>{getToolLabel(tool.tool_key, tool.tool_name)}</strong>
                      </div>

                      {/* Toggle Switch */}
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={finalState}
                          disabled={!tool.enabled}
                          onChange={() => handleToggleTool(tool.tool_key, isGranted)}
                        />
                        <span className="slider round"></span>
                      </label>

                      {/* Settings Button (Only if active) */}
                      <button
                        type="button"
                        className="tool-settings-btn"
                        disabled={!finalState}
                        onClick={() => setEditingTool(tool.tool_key)}
                        title="Berechtigungen konfigurieren"
                      >
                        ⚙️ <span style={{ fontSize: '0.9rem', marginLeft: '5px' }}>Einstellungen</span>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Save Button for Tool Changes */}
              <div className="modal-actions" style={{ marginTop: '30px' }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={(e) => handleSave(e)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#4f46e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  💾 Änderungen speichern
                </button>
              </div>
            </div>
          )}

          {/* === VIEW 3: TOOL SETTINGS (Granular) === */}
          {editingTool && (
            <div className="tool-settings-view">
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
                Hier kannst du detaillierte Berechtigungen für <strong>{editingTool === 'journal' ? 'Projektverwaltung' : getToolLabel(editingTool, companyTools.find(t => t.tool_key === editingTool)?.tool_name)}</strong> festlegen.
              </p>

              {editingTool === 'journal' ? (
                <div className="permissions-grid">
                  {(() => {
                    const currentPerms = pendingPermissionChanges[editingTool] || userToolPermissions[editingTool] || {};

                    let currentRole = 'none';
                    if (currentPerms.view_all_projects) {
                      if (currentPerms.create_projects && currentPerms.delete_projects && currentPerms.edit_projects) {
                        currentRole = 'manager';
                      } else {
                        currentRole = 'reader';
                      }
                    }

                    const setRole = (role) => {
                      let newPerms = {};
                      if (role === 'manager') {
                        newPerms = {
                          create_projects: true,
                          delete_projects: true,
                          edit_projects: true,
                          view_all_projects: true
                        };
                      } else if (role === 'reader') {
                        newPerms = {
                          create_projects: false,
                          delete_projects: false,
                          edit_projects: false,
                          view_all_projects: true
                        };
                      } else {
                        newPerms = {
                          create_projects: false,
                          delete_projects: false,
                          edit_projects: false,
                          view_all_projects: false
                        };
                      }

                      setPendingPermissionChanges({
                        ...pendingPermissionChanges,
                        [editingTool]: newPerms
                      });
                    };

                    return (
                      <div className="permissions-grid" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <label className={`permission-item ${currentRole === 'manager' ? 'selected-role' : ''}`} style={{ cursor: 'pointer', border: currentRole === 'manager' ? '2px solid #4f46e5' : '1px solid #e5e7eb', padding: '15px', borderRadius: '8px' }}>
                          <input
                            type="radio"
                            name="global_role"
                            checked={currentRole === 'manager'}
                            onChange={() => setRole('manager')}
                            style={{ marginRight: '10px' }}
                          />
                          <div>
                            <div className="perm-label">Projekt-Verwalter</div>
                            <div className="perm-sub">Darf Projekte erstellen, löschen und <strong>alle</strong> bearbeiten.</div>
                          </div>
                        </label>

                        <label className={`permission-item ${currentRole === 'reader' ? 'selected-role' : ''}`} style={{ cursor: 'pointer', border: currentRole === 'reader' ? '2px solid #4f46e5' : '1px solid #e5e7eb', padding: '15px', borderRadius: '8px' }}>
                          <input
                            type="radio"
                            name="global_role"
                            checked={currentRole === 'reader'}
                            onChange={() => setRole('reader')}
                            style={{ marginRight: '10px' }}
                          />
                          <div>
                            <div className="perm-label">Projekt-Leser</div>
                            <div className="perm-sub">Sieht <strong>alle</strong> Projekte (Lesezugriff). Bearbeiten nur bei expliziter Zuweisung.</div>
                          </div>
                        </label>

                        <label className={`permission-item ${currentRole === 'none' ? 'selected-role' : ''}`} style={{ cursor: 'pointer', border: currentRole === 'none' ? '2px solid #4f46e5' : '1px solid #e5e7eb', padding: '15px', borderRadius: '8px' }}>
                          <input
                            type="radio"
                            name="global_role"
                            checked={currentRole === 'none'}
                            onChange={() => setRole('none')}
                            style={{ marginRight: '10px' }}
                          />
                          <div>
                            <div className="perm-label">Keine globalen Rechte (Standard)</div>
                            <div className="perm-sub">Sieht nur Projekte, denen er explizit zugewiesen wurde.</div>
                          </div>
                        </label>
                      </div>
                    );
                  })()}
                </div>
              ) : editingTool === 'timesheet' ? (
                <div className="permissions-grid">
                  {(() => {
                    const currentPerms = pendingPermissionChanges[editingTool] || userToolPermissions[editingTool] || {};
                    const currentRole = currentPerms.role || 'standard';
                    const managedUsers = currentPerms.managed_users || [];

                    const setRole = (role) => {
                      setPendingPermissionChanges({
                        ...pendingPermissionChanges,
                        [editingTool]: { ...currentPerms, role, managed_users: role === 'team_lead' ? managedUsers : [] }
                      });
                    };

                    return (
                      <div className="permissions-grid" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <label className={`permission-item ${currentRole === 'manager' ? 'selected-role' : ''}`} style={{ cursor: 'pointer', border: currentRole === 'manager' ? '2px solid #4f46e5' : '1px solid #e5e7eb', padding: '15px', borderRadius: '8px' }}>
                          <input type="radio" checked={currentRole === 'manager'} onChange={() => setRole('manager')} style={{ marginRight: '10px' }} />
                          <div>
                            <div className="perm-label">Vollzugriff</div>
                            <div className="perm-sub">Voller Zugriff auf Berichte <strong>aller</strong> Mitarbeiter (Ansicht, Bearbeitung, Fremderstellung).</div>
                          </div>
                        </label>

                        <label className={`permission-item ${currentRole === 'reader' ? 'selected-role' : ''}`} style={{ cursor: 'pointer', border: currentRole === 'reader' ? '2px solid #4f46e5' : '1px solid #e5e7eb', padding: '15px', borderRadius: '8px' }}>
                          <input type="radio" checked={currentRole === 'reader'} onChange={() => setRole('reader')} style={{ marginRight: '10px' }} />
                          <div>
                            <div className="perm-label">Lesezugriff</div>
                            <div className="perm-sub">Reiner <strong>Lesezugriff</strong> auf die Nachweise aller Mitarbeiter.</div>
                          </div>
                        </label>

                        <label className={`permission-item ${currentRole === 'team_lead' ? 'selected-role' : ''}`} style={{ cursor: 'pointer', border: currentRole === 'team_lead' ? '2px solid #4f46e5' : '1px solid #e5e7eb', padding: '15px', borderRadius: '8px' }}>
                          <input type="radio" checked={currentRole === 'team_lead'} onChange={() => setRole('team_lead')} style={{ marginRight: '10px' }} />
                          <div>
                            <div className="perm-label">Detaillierte Zugriffsverwaltung</div>
                            <div className="perm-sub">Verwaltet den Zugriff individuell pro Mitarbeiter (z.B. für Team-Leiter).</div>
                          </div>
                        </label>

                        {currentRole === 'team_lead' && (
                          <div style={{ marginTop: '10px', textAlign: 'center', padding: '10px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <button
                              type="button"
                              onClick={() => setShowTeamModal(true)}
                              style={{
                                width: '100%',
                                padding: '10px 20px',
                                background: '#e0e7ff',
                                color: '#4338ca',
                                border: '1px solid #c7d2fe',
                                borderRadius: '6px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                              }}
                            >
                              👥 Zugriff verwalten ({managedUsers.length} ausgewählt)
                            </button>
                          </div>
                        )}

                        <label className={`permission-item ${currentRole === 'standard' ? 'selected-role' : ''}`} style={{ cursor: 'pointer', border: currentRole === 'standard' ? '2px solid #4f46e5' : '1px solid #e5e7eb', padding: '15px', borderRadius: '8px' }}>
                          <input type="radio" checked={currentRole === 'standard'} onChange={() => setRole('standard')} style={{ marginRight: '10px' }} />
                          <div>
                            <div className="perm-label">Keine Verwaltungsrechte</div>
                            <div className="perm-sub">Nutzer erfasst und bearbeitet nur <strong>eigene</strong> Einträge.</div>
                          </div>
                        </label>
                      </div>
                    );
                  })()}
                </div>
              ) : editingTool === 'leader' ? (
                <div className="permissions-grid">
                  {(() => {
                    const currentPerms = pendingPermissionChanges[editingTool] || userToolPermissions[editingTool] || {};
                    const accessLevel = currentPerms.accessLevel || 'full';

                    const setLevel = (level) => {
                      setPendingPermissionChanges({
                        ...pendingPermissionChanges,
                        [editingTool]: { ...currentPerms, accessLevel: level }
                      });
                    };

                    return (
                      <div className="permissions-grid" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <label className={`permission-item ${accessLevel === 'full' ? 'selected-role' : ''}`} style={{ cursor: 'pointer', border: accessLevel === 'full' ? '2px solid #4f46e5' : '1px solid #e5e7eb', padding: '15px', borderRadius: '8px' }}>
                          <input
                            type="radio"
                            name="leader_access"
                            checked={accessLevel === 'full'}
                            onChange={() => setLevel('full')}
                            style={{ marginRight: '10px' }}
                          />
                          <div>
                            <div className="perm-label">Vollzugriff</div>
                            <div className="perm-sub">Darf alle Mitarbeiter und Fahrzeuge sehen, Dispositions-Änderungen vornehmen und Termine bearbeiten.</div>
                          </div>
                        </label>

                        <label className={`permission-item ${accessLevel === 'personal_only' ? 'selected-role' : ''}`} style={{ cursor: 'pointer', border: accessLevel === 'personal_only' ? '2px solid #4f46e5' : '1px solid #e5e7eb', padding: '15px', borderRadius: '8px' }}>
                          <input
                            type="radio"
                            name="leader_access"
                            checked={accessLevel === 'personal_only'}
                            onChange={() => setLevel('personal_only')}
                            style={{ marginRight: '10px' }}
                          />
                          <div>
                            <div className="perm-label">Reine Informationsansicht</div>
                            <div className="perm-sub">Sieht nur <strong>eigene</strong> zugewiesene Einsätze. Keine Bearbeitung oder Dispositions-Rechte möglich.</div>
                          </div>
                        </label>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="placeholder-settings">
                  <p>Für dieses Tool sind noch keine feingranularen Einstellungen verfügbar.</p>
                </div>
              )}

              {pendingPermissionChanges[editingTool] && (
                <div className="modal-actions" style={{ marginTop: '30px' }}>
                  <button
                    type="button"
                    onClick={(e) => handleSave(e)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#4f46e5',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    💾 Änderungen speichern
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* TEAM LEADER CONFIG MODAL */}
      {showTeamModal && editingTool === 'timesheet' && (
        <div className="modal-overlay" onClick={() => setShowTeamModal(false)}>
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '600px', width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header" style={{ marginBottom: '15px' }}>
              <div>
                <h3 style={{ margin: 0 }}>Zugriff verwalten</h3>
              </div>
              <button className="close-modal" onClick={() => setShowTeamModal(false)}>&times;</button>
            </div>

            <div className="search-box" style={{ marginBottom: '15px', marginTop: '10px' }}>
              <input
                type="text"
                placeholder="Mitarbeiter suchen..."
                value={teamSearchTerm}
                onChange={e => setTeamSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '5px' }}>
              <div style={{ display: 'grid', gap: '8px' }}>
                {(() => {
                  const currentPerms = pendingPermissionChanges[editingTool] || userToolPermissions[editingTool] || {};
                  const managedUsers = currentPerms.managed_users || [];
                  const currentRole = currentPerms.role || 'standard';

                  const handleUserToggle = (userId, canEdit) => {
                    let newManaged = [...managedUsers];
                    const existingIdx = newManaged.findIndex(u => u.id === userId);
                    if (existingIdx >= 0) {
                      if (newManaged[existingIdx].can_edit === canEdit) newManaged.splice(existingIdx, 1);
                      else newManaged[existingIdx].can_edit = canEdit;
                    } else {
                      newManaged.push({ id: userId, can_edit: canEdit });
                    }
                    setPendingPermissionChanges({
                      ...pendingPermissionChanges,
                      [editingTool]: { ...currentPerms, role: currentRole, managed_users: newManaged }
                    });
                  };

                  const filteredTeamUsers = users.filter(
                    u => u.id !== user?.id &&
                    (u.name.toLowerCase().includes(teamSearchTerm.toLowerCase()) ||
                     u.username.toLowerCase().includes(teamSearchTerm.toLowerCase()))
                  );

                  if (filteredTeamUsers.length === 0) {
                    return <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>Keine Mitarbeiter gefunden.</p>;
                  }

                  return filteredTeamUsers.map(u => {
                    const userPerm = managedUsers.find(mu => mu.id === u.id);
                    const isSelected = !!userPerm;
                    const canEdit = isSelected ? userPerm.can_edit : false;

                    return (
                      <div
                        key={u.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '6px 15px',
                          background: '#fff',
                          borderRadius: '6px',
                          border: isSelected ? '1px solid #4f46e5' : '1px solid #d1d5db',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                      >
                        <div style={{ fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isSelected ? '#4f46e5' : '#ccc' }}></div>
                          {u.name} <span style={{ color: '#6b7280', fontSize: '0.8rem', fontWeight: 'normal' }}>({u.role === 'employee' ? 'Mitarbeiter' : u.role})</span>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            type="button"
                            onClick={() => handleUserToggle(u.id, false)}
                            style={{
                              padding: '6px 12px',
                              fontSize: '0.8rem',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              background: isSelected && !canEdit ? '#e0e7ff' : '#f9fafb',
                              color: isSelected && !canEdit ? '#4338ca' : '#4b5563',
                              cursor: 'pointer',
                              fontWeight: isSelected && !canEdit ? '600' : 'normal',
                              transition: 'all 0.15s',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Nur Lesen
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUserToggle(u.id, true)}
                            style={{
                              padding: '6px 12px',
                              fontSize: '0.8rem',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              background: isSelected && canEdit ? '#e0e7ff' : '#f9fafb',
                              color: isSelected && canEdit ? '#4338ca' : '#4b5563',
                              cursor: 'pointer',
                              fontWeight: isSelected && canEdit ? '600' : 'normal',
                              transition: 'all 0.15s',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Verwalten
                          </button>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div
              className="modal-actions"
              style={{
                marginTop: '20px',
                paddingTop: '15px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <button
                type="button"
                onClick={() => setShowTeamModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Fertig
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
